import { useState } from 'react';
import { Controller } from 'react-hook-form';
import { NavLink } from 'react-router-dom';
import copy from 'copy-to-clipboard';
import {
  DateInput,
  PickerPosition,
  InputText,
  Select,
  Autocomplete,
  IconPaper,
  IconDownload,
  IconLoader,
  Dropzone,
  IconCopyTwo,
  formatDate,
  IconAnonymizedFile,
} from '@ui/src';
import formatBalance from '@ui/src/helpers/format-balance';
import { GeneralInfoTypes } from './types';

import ROUTES from '../../routes';

import cn from './styles.module.css';

const PaymentOrderGeneral = ({
  control,
  errors,
  currency,
  currencies,
  recipientAccounts,
  recipientAccountsIsLoading,
  onSearchRecipientAccounts,
  pspPartners,
  pspPartnersIsLoading,
  onSearchPspPartners,
  pspAccountDisabled,
  pspPartnerAccounts,
  pspPartnerAccountsIsLoading,
  onSearchPspPartnerAccounts,
  status,
  confirmedAmount,
  pendingAmount,
  rejectedAmount,
  transactionId,
  orderUploadId,
  receiptUploadId,
  setReceiptUploadId,
  uploadId,
  downloadInProgress,
  onDownload,
  uploadInputRef,
  fileUploadInProgress,
  onUpload,
  uploadedFilesError,
  fields,
  uploadedReceiptDocumentsErrors,
  isEditable,
  anonymized,
  pspPartnerAccountsNextPageIsLoading,
  pspPartnerAccountsOnScroll,
  pspPartnerAccountsPagination,
}: GeneralInfoTypes) => {
  const [copyIsSuccess, setCopyIsSuccess] = useState(false);

  const onCopy = (copyValue: string) => {
    setCopyIsSuccess(true);
    copy(copyValue);

    setTimeout(() => {
      setCopyIsSuccess(false);
    }, 2000);
  };

  return (
    <div className={cn.paymentOrderForm}>
      <div className={cn.balances}>
        <div className={cn.balance}>
          <div className={cn.balanceSum}>
            {`${formatBalance(
              confirmedAmount ?? 0,
              currency,
            )} ${currency?.name}`}
          </div>
          <div className={cn.balanceTitle}>Confirmed amount</div>
        </div>
        <div className={cn.balance}>
          <div className={cn.balanceSum}>
            {`${formatBalance(pendingAmount ?? 0, currency)} ${currency?.name}`}
          </div>
          <div className={cn.balanceTitle}>Pending amount</div>
        </div>
        <div className={cn.balance}>
          <div className={cn.balanceSum}>
            {`${formatBalance(
              rejectedAmount ?? 0,
              currency,
            )} ${currency?.name}`}
          </div>
          <div className={cn.balanceTitle}>Rejected amount</div>
        </div>
      </div>
      <div className={cn.formGroup}>
        <Controller
          name="orderCreatedAt"
          control={control}
          render={({ field: { value, onChange } }) => (
            <DateInput
              title="Date created"
              value={value}
              onChange={(e) => onChange(e.toISOString())}
              placeholder="Enter date created"
              pickerPisition={PickerPosition.RIGHT}
              className={`${cn.transactionDateInput} ${
                errors?.orderCreatedAt?.message
                  ? cn.transactionDateInputError
                  : ''
              }`}
              onReset={() => onChange(undefined)}
              error={errors?.orderCreatedAt?.message}
              disabled={
                status === 'ACCEPTED' ||
                status === 'REJECTED' ||
                status === 'AML_REJ' ||
                !isEditable
              }
              fullWidth
              withoutCustomSelector
            />
          )}
        />
        <button
          type="button"
          className={`${cn.copyButton} ${cn.autocompleteCopyButton} ${
            copyIsSuccess ? cn.success : ''
          }`}
          onClick={() => onCopy(formatDate(fields?.orderCreatedAt || ''))}
          aria-label="Copy"
        >
          <IconCopyTwo />
        </button>
      </div>
      <div className={`${cn.formGroup} ${cn.flex}`}>
        <div className={cn.flexItem}>
          <Controller
            name="sum"
            control={control}
            render={({ field: { name, value, onChange } }) => (
              <InputText
                name={name}
                value={value}
                title="Payment amount"
                placeholder="Enter amount"
                onChange={onChange}
                error={errors?.sum?.message}
                disabled={
                  status === 'ACCEPTED' ||
                  status === 'REJECTED' ||
                  status === 'AML_REJ' ||
                  !isEditable
                }
                currency={currency}
                amountInput
              />
            )}
          />
          <button
            type="button"
            className={`${cn.copyButton} ${cn.autocompleteCopyButton} ${
              copyIsSuccess ? cn.success : ''
            }`}
            onClick={() =>
              onCopy(
                `${formatBalance(Number(fields?.sum))} ${
                  currencies?.find((item) => item.value === fields?.currencyId)
                    ?.name
                }`,
              )
            }
            aria-label="Copy"
          >
            <IconCopyTwo />
          </button>
        </div>
        <div className={cn.flexItem}>
          <Controller
            name="currencyId"
            control={control}
            render={({ field: { name, value, onChange } }) => (
              <Select
                name={name}
                value={value}
                list={currencies || []}
                onChange={onChange}
                error={errors?.currencyId?.message}
                title="Payment currency"
                disabled={
                  status === 'ACCEPTED' ||
                  status === 'REJECTED' ||
                  status === 'AML_REJ' ||
                  !isEditable
                }
                placeholder="Select currency"
                fullWidth
              />
            )}
          />
          <button
            type="button"
            className={`${cn.copyButton} ${cn.autocompleteCopyButton} ${
              copyIsSuccess ? cn.success : ''
            }`}
            onClick={() =>
              onCopy(
                `${formatBalance(Number(fields?.sum))} ${
                  currencies?.find((item) => item.value === fields?.currencyId)
                    ?.name
                }`,
              )
            }
            aria-label="Copy"
          >
            <IconCopyTwo />
          </button>
        </div>
      </div>
      <div className={cn.formGroup}>
        <Controller
          name="recepientAccountId"
          control={control}
          render={({ field: { name, value, onChange } }) => (
            <Autocomplete
              title="Beneficiary"
              list={recipientAccounts}
              isLoading={recipientAccountsIsLoading}
              value={value}
              placeholder="Select beneficiary"
              name={name}
              onChange={onChange}
              onSearch={(searchText) => onSearchRecipientAccounts(searchText)}
              disabled={
                status === 'ACCEPTED' ||
                status === 'REJECTED' ||
                status === 'AML_REJ' ||
                !isEditable
              }
              error={errors?.recepientAccountId?.message}
              withClearButton
              fullWidth
            />
          )}
        />
        <button
          type="button"
          className={`${cn.copyButton} ${cn.autocompleteCopyButton} ${
            copyIsSuccess ? cn.success : ''
          }`}
          onClick={() =>
            onCopy(
              recipientAccounts?.find(
                (item) => item?.value === fields?.recepientAccountId,
              )?.name || '-',
            )
          }
          aria-label="Copy"
        >
          <IconCopyTwo />
        </button>
      </div>
      <div className={cn.formGroup}>
        <Controller
          name="pspPartnerId"
          control={control}
          render={({ field: { name, value, onChange } }) => (
            <Autocomplete
              title="Partner bank"
              list={pspPartners}
              isLoading={pspPartnersIsLoading}
              value={value}
              placeholder="Select Partner bank"
              name={name}
              onChange={onChange}
              onSearch={(searchText) => onSearchPspPartners(searchText)}
              disabled={
                status === 'ACCEPTED' ||
                status === 'REJECTED' ||
                status === 'AML_REJ' ||
                !isEditable
              }
              error={errors?.pspPartnerId?.message}
              withClearButton
              fullWidth
            />
          )}
        />
        <button
          type="button"
          className={`${cn.copyButton} ${cn.autocompleteCopyButton} ${
            copyIsSuccess ? cn.success : ''
          }`}
          onClick={() =>
            onCopy(
              pspPartners?.find((item) => item?.value === fields?.pspPartnerId)
                ?.name || '-',
            )
          }
          aria-label="Copy"
        >
          <IconCopyTwo />
        </button>
      </div>
      <div className={cn.formGroup}>
        <Controller
          name="pspPartnerAccountId"
          control={control}
          render={({ field: { name, value, onChange } }) => (
            <Autocomplete
              title="Partner Bank Account"
              list={pspPartnerAccounts}
              isLoading={pspPartnerAccountsIsLoading}
              value={value}
              placeholder="Select Partner Bank Account"
              name={name}
              onChange={onChange}
              onSearch={(searchText) => onSearchPspPartnerAccounts(searchText)}
              error={errors?.pspPartnerAccountId?.message}
              disabled={
                status === 'ACCEPTED' ||
                status === 'REJECTED' ||
                status === 'AML_REJ' ||
                pspAccountDisabled ||
                !isEditable
              }
              onInfinateScroll={
                pspPartnerAccountsOnScroll
                  ? () => pspPartnerAccountsOnScroll()
                  : undefined
              }
              hasNextPage={pspPartnerAccountsPagination?.hasNextPage}
              withInfiniteScroll={Boolean(pspPartnerAccountsOnScroll)}
              nextPageIsLoading={pspPartnerAccountsNextPageIsLoading}
              withSuffix
              withClearButton
              fullWidth
            />
          )}
        />
        <button
          type="button"
          className={`${cn.copyButton} ${cn.autocompleteCopyButton} ${
            copyIsSuccess ? cn.success : ''
          }`}
          onClick={() =>
            onCopy(
              pspPartnerAccounts?.find(
                (item) => item?.value === fields?.pspPartnerAccountId,
              )?.suffix || '-',
            )
          }
          aria-label="Copy"
        >
          <IconCopyTwo />
        </button>
      </div>
      <div className={cn.formGroup}>
        <Controller
          name="orderNumber"
          control={control}
          render={({ field: { name, value, onChange } }) => (
            <InputText
              name={name}
              value={value}
              title="Cash bag number"
              placeholder="Enter Cash bag number"
              onChange={onChange}
              error={errors?.orderNumber?.message}
              disabled={
                status === 'ACCEPTED' ||
                status === 'REJECTED' ||
                status === 'AML_REJ' ||
                !isEditable
              }
            />
          )}
        />
        <button
          type="button"
          className={`${cn.copyButton} ${cn.autocompleteCopyButton} ${
            copyIsSuccess ? cn.success : ''
          }`}
          onClick={() => onCopy(fields?.orderNumber || '-')}
          aria-label="Copy"
        >
          <IconCopyTwo />
        </button>
      </div>
      <div className={cn.formGroup}>
        <InputText
          name="id"
          value={transactionId}
          title="Payment Order ID"
          placeholder=""
          disabled
        />
        <button
          type="button"
          className={`${cn.copyButton} ${cn.autocompleteCopyButton} ${
            copyIsSuccess ? cn.success : ''
          }`}
          onClick={() => onCopy(transactionId || '-')}
          aria-label="Copy"
        >
          <IconCopyTwo />
        </button>
      </div>
      <div className={cn.separator} />
      <div className={cn.documentsWrapper}>
        <div className={cn.uploadTitle}>Compliance batch</div>
        {anonymized && (
          <div className={cn.documentItem}>
            <div className={cn.documentItemLeft}>
              <div className={cn.documentItemLeftIconWrapper}>
                <IconAnonymizedFile width={20} height={20} />
              </div>
              <div className={cn.documentItemLeftName}>
                <div className={cn.anonymized}>
                  Anonymized on {formatDate(anonymized, true)}
                </div>
              </div>
            </div>
          </div>
        )}
        {orderUploadId && !anonymized && (
          <div className={cn.documentItem}>
            <div className={cn.documentItemLeft}>
              <div className={cn.documentItemLeftIconWrapper}>
                <IconPaper width={20} height={20} />
              </div>
              <div className={cn.documentItemLeftName}>
                <div>Compliance batch</div>
              </div>
            </div>
            <div className={cn.documentItemRight}>
              <button
                type="button"
                className={cn.downloadButton}
                disabled={downloadInProgress}
                onClick={() => onDownload(orderUploadId, 'list')}
              >
                {downloadInProgress && uploadId === orderUploadId ? (
                  <IconLoader width={14} height={14} />
                ) : (
                  <IconDownload />
                )}
              </button>
            </div>
          </div>
        )}
        <div className={`${cn.uploadTitle} ${cn.marginTop}`}>
          Cash collection receipt
        </div>
        {(status === 'ACCEPTED' ||
          status === 'REJECTED' ||
          status === 'AML_REJ') &&
          !receiptUploadId && (
            <div className={cn.receiptEmpty}>
              No Cash collection receipt yet
            </div>
          )}
        {!receiptUploadId &&
          status !== 'ACCEPTED' &&
          status !== 'REJECTED' &&
          status !== 'AML_REJ' &&
          isEditable && (
            <>
              <Dropzone
                inputRef={uploadInputRef}
                onUpload={onUpload}
                fileUploadInProgress={fileUploadInProgress}
                dropzoneText="Drag and drop receipt file here or select file"
                dropzoneClassName={cn.dropzoneWrapper}
              />
              {uploadedFilesError && uploadedFilesError.length > 0 && (
                <ul className={cn.uploadDocumentErrors}>
                  {uploadedFilesError.map((item) => (
                    <li key={item}>{`- ${item}`}</li>
                  ))}
                </ul>
              )}
            </>
          )}
        {receiptUploadId && (
          <div className={cn.documentItem}>
            <div className={cn.documentItemLeft}>
              <div className={cn.documentItemLeftIconWrapper}>
                <IconPaper width={20} height={20} />
              </div>
              <div className={cn.documentItemLeftName}>
                <div>Cash collection receipt</div>
              </div>
            </div>
            <div className={cn.documentItemRight}>
              {status !== 'ACCEPTED' &&
                status !== 'REJECTED' &&
                status !== 'AML_REJ' &&
                isEditable && (
                  <button
                    type="button"
                    className={cn.downloadButton}
                    onClick={() => setReceiptUploadId('')}
                  >
                    Upload new
                  </button>
                )}

              <button
                type="button"
                className={cn.downloadButton}
                disabled={downloadInProgress}
                onClick={() => onDownload(receiptUploadId, 'receipt')}
              >
                {downloadInProgress && uploadId === receiptUploadId ? (
                  <IconLoader width={14} height={14} />
                ) : (
                  <IconDownload />
                )}
              </button>
            </div>
          </div>
        )}
        {uploadedReceiptDocumentsErrors &&
          uploadedReceiptDocumentsErrors?.length > 0 && (
            <ul className={cn.uploadDocumentsErrors}>
              {uploadedReceiptDocumentsErrors?.map((item, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={`${item.name}-${index}`}>
                  - The document <strong>{item.name}</strong> was not uploaded.
                  <br />
                  <span>
                    <strong>Reason:</strong> {item.error}
                  </span>
                </li>
              ))}
            </ul>
          )}
      </div>
      <NavLink
        to={`${ROUTES.INCOMING}?paymentOrderId=${transactionId}`}
        target="_blank"
        className={cn.transactionLink}
      >
        Go to payment order transactions list
      </NavLink>
    </div>
  );
};

export default PaymentOrderGeneral;
